import React, { forwardRef, useImperativeHandle, useState, useContext, useEffect } from "react";
import { Dialog, DialogActions, DialogContent, Typography, useMediaQuery } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Close } from "@mui/icons-material";
import MButton from "components/momentum/MButton";
import GridEventContext from "components/context/GridEventContext";
import { RowNode } from "ag-grid-community";
import {
  AOV_COLLECTION_COLUMN,
  AOV_COLLECTION_COLUMN_STATE,
  COLLECTION_COLUMN,
  COLLECTION_COLUMN_STATE,
  CURRENT_ACCOUNTKEY,
  PORTFOLIO_AOV_SHOW,
  PRE_SELECTED_ACCOUNT,
} from "constant";
import {
  checkSelectAll,
  getBulkActionAccounts,
  getItemFromLocalStorage,
  transformAgGridRequest,
} from "utils";
// import GridCounter from "components/app/GridCounter";
import { toast } from "react-toastify";
import { SelectAccount } from "types/summary";
import SearchBar from "./SearchBar";
import GridViewFunctionBar from "./GridViewFunctionBar";
import GridViewTable from "./GridViewTable";
import { CollectionRevenueContext } from "components/portfolio/cellRenderer/CollectionRevenueCellRenderer";
import dynamic from "next/dynamic";
import ToggleAOV from "components/portfolio/GridViewFunction/ToggleAOV";
import { AuthProvider } from "pages";
// import { useAxios } from "myAxios";

const useStyles = makeStyles(() => ({
  popWrapper: {
    width: "69.4%",
    background: "#FFFFFF",
    // border:"1px solid #DEDEDE",
    boxSizing: "border-box",
    borderRadius: 4,
  },
  container: {
    width: "66.4%",
  },
  popTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    height: 72,
    fontFamily: "Arial",
    fontSize: 24,
    fontWeight: "bold",
    color: "#171B1F",
    padding: "0 18px 0 24px",
    borderBottom: "1px solid #d2d2d2",
  },
  contentTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  title: {
    color: "#24282B",
    fontSize: 16,
  },
}));

const AccountSelectPop = forwardRef<
  { delete: (id: string) => void },
  {
    open: boolean;
    onClose: () => void;
    getRes: (data: SelectAccount[]) => void;
    preSelectedIds: string[];
    currentAccountId: string;
    cavBuId: number;
    ciscoCsmId: string;
    fixedAccountId: string;
  }
>(({ open, onClose, getRes, preSelectedIds, cavBuId, ciscoCsmId, fixedAccountId }, ref) => {
  const classes = useStyles();
  // const axios = useAxios();
  const [totalSelect, setTotalSelect] = useState<SelectAccount[]>([]);
  const [isSelectAll, setIsSelectAll] = useState(checkSelectAll());
  const [_totalCount, setTotalCount] = useState(0);
  const { gridEvent } = useContext(GridEventContext);
  const smallScreen = useMediaQuery("(max-width:1280px)");
  const [updating, setUpdating] = useState(false);
  const [isFullScreen, _setIsFullScreen] = useState(false);
  const [groupCol, _setGroupCol] = useState("none");
  const [collectionColumns, setCollectionColumns] = useState<string[]>(() => {
    if (typeof window === "undefined") return [];
    const state = localStorage.getItem(COLLECTION_COLUMN_STATE);
    if (state) {
      try {
        const res: string[] = JSON.parse(state);
        if (res.some((x) => !COLLECTION_COLUMN.includes(x))) {
          return COLLECTION_COLUMN;
        } else {
          return res;
        }
      } catch (_e) {
        return COLLECTION_COLUMN;
      }
    }
    return COLLECTION_COLUMN;
  });
  const [aovCollectionColumns, setAOVCollectionColumns] = useState<string[]>(() => {
    if (typeof window === "undefined") return [];
    const state = localStorage.getItem(AOV_COLLECTION_COLUMN_STATE);
    if (state) {
      try {
        const res: string[] = JSON.parse(state);
        if (res.some((x) => !AOV_COLLECTION_COLUMN.includes(x))) {
          return AOV_COLLECTION_COLUMN;
        } else {
          return res;
        }
      } catch (_e) {
        return AOV_COLLECTION_COLUMN;
      }
    }
    return AOV_COLLECTION_COLUMN;
  });
  // const [isSaved, setIsSaved] = useState(false);
  const authDetail = useContext(AuthProvider);
  const [showAOV, setShowAOV] = useState(() => {
    return getItemFromLocalStorage(PORTFOLIO_AOV_SHOW, false);
  });

  const getShowAovStatus = (flag: boolean) => {
    setShowAOV(flag);
  };

  useImperativeHandle(ref, () => ({
    delete: (id) => {
      gridEvent?.api?.getRowNode(id)?.setSelected(false);
    },
  }));

  useEffect(() => {
    gridEvent?.api?.purgeServerSideCache();
  }, [fixedAccountId]);

  useEffect(() => {
    const handleSelectAll = (e: any) => setIsSelectAll(e.detail.checked);
    const handleTotalCount = (e: any) => {
      setTotalCount(e.detail.totalCount);
    };
    if (gridEvent) {
      const rowNodes: RowNode[] = [];
      gridEvent.api.redrawRows({ rowNodes });
      addEventListener("SELECT_ALL_CHECKBOX", handleSelectAll);
      addEventListener("DATA_LOADED", handleTotalCount);
    }
    return () => {
      removeEventListener("SELECT_ALL_CHECKBOX", handleSelectAll);
      removeEventListener("DATA_LOADED", handleTotalCount);
    };
  }, [gridEvent]);

  useEffect(() => {
    const preSelected = sessionStorage.getItem(PRE_SELECTED_ACCOUNT);
    if (preSelected) {
      const parsedParams = JSON.parse(preSelected);
      setTotalSelect(parsedParams);
    }
  }, [gridEvent]);

  const handleClose = () => {
    onClose();
    // preSelectedIds is initial select ids
    gridEvent?.api?.deselectAll();
    // if (!isSaved) {
    preSelectedIds?.map((id) => {
      const node = gridEvent?.api?.getRowNode(id);
      if (node) {
        node.setSelected(true);
      }
    });
    // }
  };

  const submitSelectAccount = async () => {
    if (isSelectAll) {
      // if (totalCount > 100 || totalSelect.length > 100) {
      //   toast.error("Maximum 100");
      //   return;
      // }
      const toastId = toast.info("Fetching all the accounts, this might take a while", {
        position: "top-center",
        autoClose: false,
      });
      const filterModel = gridEvent?.api.getFilterModel();
      try {
        setUpdating(true);
        const { data } = await getBulkActionAccounts<any[]>(
          "/api/dataservice/v1/accounts/smart-search",
          "POST",
          undefined,
          transformAgGridRequest(
            { filterModel },
            [
              {
                field: "successPlanFilter",
                operator: "eq",
                value: sessionStorage.getItem(CURRENT_ACCOUNTKEY),
              },
            ],
            setCollectionColumns,
            setAOVCollectionColumns,
            true
          )
        );
        const selIds = totalSelect.map((i) => i.accountKey);
        const selectedData = [
          // ...totalSelect,
          ...data
            .map((x) => ({
              ...x,
              accountId: x.accountId,
              accountKey: x.accountKey,
              accountName: x.accountName,
            }))
            .filter((i) => !selIds.includes(i.id)),
        ];
        getRes(selectedData);
        const newData: any = [];
        selectedData.map((i) => {
          newData.push({ accountKey: i.accountKey });
        });
        sessionStorage.setItem(PRE_SELECTED_ACCOUNT, JSON.stringify(newData));
        setUpdating(false);
      } catch (e) {
        toast.error(e?.response?.data?.message || "Failed to get contacts");
        setUpdating(false);
        return;
      }

      toast.dismiss(toastId);
    } else {
      // get subscriptionData and merge into selectedData
      // const params = {
      //   includeSite: true,
      //   includeSku: true,
      //   includeSiteInactive: true,
      //   includeRenewal: true,
      //   includeHealthInfo: true,
      //   usingBundle: true,
      //   latestSubscriptionOnly: 1,
      // };
      // const query: string[] = [];
      // Object.entries(params).forEach(([p, idx]) => {
      //   if (p === "summaryOrder") {
      //     query.push("latestSubscriptionOnly=1");
      //     query.push("sorts=offerCode:asc,serviceStartDate:asc");
      //   } else {
      //     query.push(`${p} = ${idx}`);
      //   }
      // });
      // totalSelect?.map((a: any) => {
      //   axios
      //     .get(`/api/dataservice/v1/accounts/${a.accountId}/subscriptions`, {
      //       params: params,
      //     })
      //     .then((res) => {
      //       const subData = res.data;
      //       a.subscription = subData;
      //     });
      // });
      getRes(totalSelect);
      const newData: any = [];
      totalSelect.map((i) => {
        newData.push({ accountKey: i.accountKey });
      });
      sessionStorage.setItem(PRE_SELECTED_ACCOUNT, JSON.stringify(newData));
    }
    // setIsSaved(true);
    onClose();
  };

  return (
    <CollectionRevenueContext.Provider
      value={{
        collectionColumns,
        setCollectionColumns,
        aovCollectionColumns,
        setAOVCollectionColumns,
      }}
    >
      <div
        className={classes.popWrapper}
        style={{
          border: open ? "1px solid #DEDEDE" : "none",
        }}
      >
        <Dialog
          open={open}
          onClose={handleClose}
          // maxWidth="xl"
          keepMounted={true}
          fullScreen={true}
        >
          <div className={classes.popTop}>
            <h2 style={{ fontSize: 20, fontWeight: 600 }}>Account Selection</h2>
            <Close onClick={handleClose} />
          </div>
          <DialogContent
            style={{
              padding: "0 24px 8px 24px",
              filter: updating ? "opacity(0.5)" : undefined,
              pointerEvents: updating ? "none" : "inherit",
            }}
          >
            <div className={classes.contentTop}>
              <Typography className={classes.title}>Please Select Account</Typography>
              <div style={{ margin: "14px 0" }}>
                <SearchBar />
              </div>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: authDetail?.toggles?.["viewAov"] ? "space-between" : "end",
              }}
            >
              {authDetail?.toggles?.["viewAov"] && (
                <ToggleAOV getShowAovStatus={getShowAovStatus} />
              )}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "end",
                  margin: "0px 26px 14px 0px",
                }}
              >
                <GridViewFunctionBar />
              </div>
            </div>
            <div
              style={{
                display: "flex",
                height: smallScreen
                  ? `${document.body.clientHeight - 260}px`
                  : "calc(100vh - 260px)",
                minHeight: "460px",
              }}
            >
              <GridViewTable
                isFullScreen={isFullScreen}
                embedded={true}
                setCollectionColumns={setCollectionColumns}
                setAOVCollectionColumns={setAOVCollectionColumns}
                groupCol={groupCol}
                cavBuId={cavBuId}
                ciscoCsmId={ciscoCsmId}
                fixedAccountId={fixedAccountId}
                setTotalSelect={setTotalSelect}
                preSelected={preSelectedIds}
                showAOV={showAOV}
              />
            </div>
          </DialogContent>
          <DialogActions
            style={{
              justifyContent: "flex-end",
              padding: "8px 24px",
            }}
          >
            <div>
              {/* <MButton onClick={handleClose} buttonType="blue-reverse">
                Cancel
              </MButton> */}
              <MButton
                type={"button"}
                onClick={submitSelectAccount}
                buttonType="blue"
                buttonSize={36}
                disabled={updating}
              >
                Save
              </MButton>
            </div>
          </DialogActions>
        </Dialog>
      </div>
    </CollectionRevenueContext.Provider>
  );
});

// export default AccountSelectPop;
export default dynamic(() => Promise.resolve(AccountSelectPop), {
  ssr: false,
});
